/**
 * What each plan includes — the builder side of the rule (Dawn, 2026-09-29). The server's twin is
 * services/api-gateway/src/lib/entitlements.ts, tested against the same table of cases, and it is
 * the one that actually refuses (402 `upgrade_required`); this side lets the builder say so
 * before anything is sent, and in the same words.
 *
 * Pages: Free = the Home page · Starter = 8 · Pro and above = unlimited. The Blog, Privacy Policy
 * and Terms are included on every plan and never count. Blog posts: 50 below Pro, then unlimited.
 */

// Self-contained on purpose (no '@/…' imports): the gateway's mirror test imports THIS file and
// checks it against its own copy (api-gateway lib/entitlements.mirror.test.ts). The gateway builds
// standalone in Docker, so it cannot import a shared package — the mirror test is what stops the
// two drifting. PLAN_LIMITS (types.ts) reads its page counts from here.
export const FREE_PAGE_LIMIT = 1;
export const STARTER_PAGE_LIMIT = 8;

const RANK: Record<string, number> = { free: 0, starter: 1, pro: 2, growth: 3, agency: 3, brokerage: 4 };

export function planRank(plan: unknown): number {
  return RANK[String(plan ?? '').toLowerCase()] ?? 0;
}

/**
 * 'launch': the SITE plan of an unpaid MyStateMLS-launched account (the gateway's LAUNCH_PLAN, sent
 * as GET /auth/me `tenant.site_plan`). Free, plus About, Contact and the home page's own listings.
 */
export const LAUNCH_PLAN = 'launch';
const LAUNCH_PAGE_LIMIT = 3;
const isLaunchPlan = (plan: unknown) => String(plan ?? '').toLowerCase() === LAUNCH_PLAN;

/** Content pages a plan may have (Blog, Privacy and Terms never count). */
export function pageLimitForPlan(plan: unknown): number {
  if (isLaunchPlan(plan)) return LAUNCH_PAGE_LIMIT;
  const r = planRank(plan);
  if (r >= 2) return Infinity;
  return r === 1 ? STARTER_PAGE_LIMIT : FREE_PAGE_LIMIT;
}

const LEGAL_SLUGS = new Set([
  'privacy',
  'privacy-policy',
  'terms',
  'terms-of-use',
  'terms-of-service',
  'terms-and-conditions',
]);

/** Privacy Policy / Terms pages: included on every plan, never counted. */
export function isLegalPageSlug(slug: unknown): boolean {
  return LEGAL_SLUGS.has(String(slug ?? '').trim().toLowerCase().replace(/^\/+|\/+$/g, ''));
}

/**
 * The Blog page (/blog): included on every plan and never counted, like Privacy / Terms. Free sites
 * keep their blog (Dawn, 2026-10-01): writing more posts is what runs out their AI credits, which
 * asks them to buy more.
 */
export function isBlogPageSlug(slug: unknown): boolean {
  return String(slug ?? '').trim().toLowerCase().replace(/^\/+|\/+$/g, '') === 'blog';
}

/**
 * The automated pages (/agents, /communities, /sold, /coming-soon — and /blog above): routes the
 * site serves from its own data whether or not a page row exists. A row there only holds the
 * page's editable text around its live list (seeded by the gateway, healed by GET /pages), so it
 * is not an extra page and never counts — like Blog.
 */
const AUTOMATED_PAGE_SLUGS = new Set(['agents', 'communities', 'sold', 'coming-soon']);

export function isAutomatedPageSlug(slug: unknown): boolean {
  return AUTOMATED_PAGE_SLUGS.has(String(slug ?? '').trim().toLowerCase().replace(/^\/+|\/+$/g, ''));
}

/** Does this page count toward the plan's page allowance? Every page but Blog, Privacy, Terms and the automated pages. */
export function pageCountsTowardLimit(slug: unknown): boolean {
  return !isLegalPageSlug(slug) && !isBlogPageSlug(slug) && !isAutomatedPageSlug(slug);
}

/** How many pages count toward the plan's allowance. */
export function countContentPages(pages: ReadonlyArray<{ slug?: unknown }>): number {
  return pages.filter((p) => pageCountsTowardLimit(p.slug)).length;
}

/** Can one more page be added? A Blog, Privacy or Terms page always can. */
export function canAddPage(plan: unknown, pages: ReadonlyArray<{ slug?: unknown }>, newSlug?: unknown): boolean {
  if (newSlug !== undefined && !pageCountsTowardLimit(newSlug)) return true;
  return countContentPages(pages) < pageLimitForPlan(plan);
}

/**
 * Blog posts (Dawn, 2026-10-02): the Blog isn't a page and never counts toward the page allowance,
 * but posts have their own allowance — 50 on Agent IDX Essentials (and Free, so Free never gets
 * more than a paid plan), unlimited from Agent IDX Growth up. The unit is a post in
 * `content.blog_posts`, drafts included: a draft is saved there too, and publishing one must not
 * be the step that is refused.
 */
export const BLOG_POST_LIMIT = 50;

export function blogPostLimitForPlan(plan: unknown): number {
  return planRank(plan) >= 2 ? Infinity : BLOG_POST_LIMIT;
}

/**
 * May a save take the site from `before` posts to `after`? Within the allowance, or not adding any
 * — a site already over it (a downgrade) can still edit and delete its posts, just not add.
 */
export function blogPostsAllowed(plan: unknown, before: number, after: number): boolean {
  return after <= blogPostLimitForPlan(plan) || after <= before;
}

/**
 * The plan names customers see on the Billing page (apps/builder/src/lib/plans.ts `name`): the
 * `starter` tier is "Agent IDX Essentials", `pro` is "Agent IDX Growth" and `agency` is "Brokerage
 * Growth". There is no plan called just "Growth" (Dawn, 2026-09-29) — every message below uses the
 * full names, never the tier ids.
 */
export const STARTER_NAME = 'Agent IDX Essentials';
export const PRO_NAME = 'Agent IDX Growth';
export const AGENCY_NAME = 'Brokerage Growth';
/** Where a Pro-and-up feature is included. */
const PRO_AND_UP = `${PRO_NAME} and ${AGENCY_NAME}`;

/** The words a person sees when a page is refused — the server says the same thing. */
export function pageLimitMessage(plan: unknown): string {
  if (isLaunchPlan(plan)) {
    return 'Your site includes your Home, About and Contact pages (plus your Privacy Policy and Terms pages). Choose a plan to add more pages.';
  }
  return planRank(plan) >= 1
    ? `Your ${STARTER_NAME} plan includes up to 8 pages (plus your Blog, Privacy Policy and Terms pages). Upgrade to ${PRO_NAME} for unlimited pages.`
    : 'Your Free plan includes your Home page (plus your Blog, Privacy Policy and Terms pages). Upgrade to add more pages.';
}

/** The words a person sees when a new blog post is refused — the same on both sides. */
export function blogPostLimitMessage(plan: unknown): string {
  return planRank(plan) >= 1
    ? `Your ${STARTER_NAME} plan includes up to ${BLOG_POST_LIMIT} blog posts. Delete an older post to add a new one, or upgrade to ${PRO_NAME} for unlimited posts.`
    : `Your Free plan includes up to ${BLOG_POST_LIMIT} blog posts. Delete an older post to add a new one, or upgrade to add more.`;
}

/** Where "Upgrade" goes. */
export const BILLING_PATH = '/billing';

/** Did the server refuse this because the plan doesn't include it? (402 `upgrade_required`) */
export function isUpgradeRequired(err: unknown): boolean {
  return (err as { serverCode?: string } | null)?.serverCode === 'upgrade_required';
}

// ── Integrations (Dawn, 2026-09-29) ─────────────────────────────────────────────────────────────
// Free: none — not even analytics, verification tags or pixels.
// Starter: ONE MLS connection, maps (Mapbox), email via Resend, SMS via Twilio. Nothing else.
// Pro and above: everything, including more than one MLS.

/** The integrations Starter includes. Every other integration is Pro and above. */
// Connected reviews (Google, Zillow, RealSatisfied) are Starter and up too (2026-10-13 default; Free stays none).
const STARTER_INTEGRATIONS = new Set(['mls', 'mls_reso', 'mls_idx', 'mapbox', 'email_resend', 'sms_twilio', 'reviews_google', 'reviews_zillow', 'reviews_realsatisfied', 'crm_webhook']);

/** Does this plan include this integration type? ('mls' = an MLS connection of any kind.) */
export function integrationAllowed(plan: unknown, type: string): boolean {
  const r = planRank(plan);
  if (r >= 2) return true;
  return r === 1 && STARTER_INTEGRATIONS.has(type);
}

/** How many MLS connections a plan may have. */
export function mlsConnectionLimit(plan: unknown): number {
  const r = planRank(plan);
  if (r >= 2) return Infinity;
  return r === 1 ? 1 : 0;
}

const INTEGRATION_LABELS: Record<string, string> = {
  mls: 'an MLS connection',
  mls_reso: 'an MLS connection',
  mls_idx: 'an MLS connection',
  mapbox: 'maps',
  email_resend: 'email (Resend)',
  sms_twilio: 'text messages (Twilio)',
  email_smtp: 'email (SMTP)',
  email_sendgrid: 'email (SendGrid)',
  email_mailgun: 'email (Mailgun)',
  email_postmark: 'email (Postmark)',
  email_mandrill: 'email (Mandrill)',
  slack: 'Slack',
  google_analytics: 'Google Analytics',
  facebook_pixel: 'the Facebook pixel',
  google_tag_manager: 'Google Tag Manager',
  google_ads: 'Google Ads',
  google_search_console: 'Google Search Console',
  bing_webmaster: 'Bing Webmaster Tools',
  microsoft_clarity: 'Microsoft Clarity',
  custom_head: 'custom tracking code',
  consent_banner: 'the cookie consent banner',
  ai_crawler_policy: 'AI search settings',
  directories: 'listing directories',
  custom_api: 'custom API connections',
  reviews_google: 'Google reviews',
  reviews_zillow: 'Zillow reviews',
  reviews_realsatisfied: 'RealSatisfied reviews',
  crm_webhook: 'sending leads to your CRM',
};

export function integrationLabel(type: string): string {
  return INTEGRATION_LABELS[type] ?? 'this integration';
}

/** The words a person sees when an integration is refused — the server says the same thing (mirror-tested). */
export function integrationMessage(plan: unknown, type: string): string {
  const label = integrationLabel(type);
  if (planRank(plan) === 0) return `Integrations are included on paid plans. Upgrade to connect ${label}.`;
  return `Connecting ${label} is included on ${PRO_AND_UP}. Upgrade to add it.`;
}

/** Does an MLS connection with this access status use up the allowance? Revoked, denied and
 *  disabled connections don't — after disconnecting, the account can connect one again. */
export function countsTowardMlsAllowance(accessStatus: unknown): boolean {
  return !/revok|denied|disabled/i.test(String(accessStatus ?? ''));
}

/** The MLSs an account already uses, by catalogue id (or the MLS name when the catalogue doesn't
 *  know it), plus any chosen in this wizard session. Revoked, denied and disabled connections
 *  don't count — the server's rule (services/api-gateway/src/lib/mls-allowance.ts). */
export function mlsInUse(
  connections: ReadonlyArray<{ catalog_id?: string | null; mls?: string | null; access_status?: unknown }>,
  chosenCatalogIds: readonly string[] = [],
): Set<string> {
  const used = new Set<string>();
  for (const c of connections) {
    if (!countsTowardMlsAllowance(c.access_status)) continue;
    used.add(c.catalog_id ?? `mls:${String(c.mls ?? '').trim().toLowerCase()}`);
  }
  for (const id of chosenCatalogIds) used.add(id);
  return used;
}

/** May this MLS be connected? Reconnecting or re-keying one already in use always may; a new one
 *  needs room under the plan's allowance. */
export function hasRoomForMls(plan: unknown, inUse: ReadonlySet<string>, catalogId: string): boolean {
  return inUse.has(catalogId) || inUse.size < mlsConnectionLimit(plan);
}

/** Starter has its one MLS connection and asked for another. */
export function mlsLimitMessage(plan: unknown): string {
  return planRank(plan) === 0
    ? integrationMessage(plan, 'mls')
    : `Your ${STARTER_NAME} plan includes one MLS connection. Upgrade to ${PRO_NAME} to connect more.`;
}

/**
 * A locked panel's words, for a group of integrations (builder-only — the server refuses one
 * integration at a time with integrationMessage). `noun` is plural: "analytics, pixels and
 * search verification".
 */
export function lockedPanelMessage(plan: unknown, noun: string): string {
  if (planRank(plan) === 0) return `Integrations are included on paid plans. Upgrade to add ${noun}.`;
  return `${noun.charAt(0).toUpperCase()}${noun.slice(1)} are included on ${PRO_AND_UP}. Upgrade to add them.`;
}

// ── Plan features (Dawn, 2026-09-29) ────────────────────────────────────────────────────────────
// Communities:    Agent IDX Growth and up.
// SEO tools:      Agent IDX Growth and up — the TOOLS only (site health, AI-written titles and
//                 descriptions, redirects; Search Console is an integration). Every plan still
//                 writes its own page titles and descriptions.
// Custom domain:  every paid plan, from Agent IDX Essentials up.

export type PlanFeature = 'communities' | 'seo_tools' | 'custom_domain';

const FEATURE_MIN_RANK: Record<PlanFeature, number> = { communities: 2, seo_tools: 2, custom_domain: 1 };

/** Does this plan include this feature? */
export function featureAllowed(plan: unknown, feature: PlanFeature): boolean {
  return planRank(plan) >= FEATURE_MIN_RANK[feature];
}

/** The words a person sees when a feature is refused — the same on both sides. */
export function featureMessage(feature: PlanFeature): string {
  switch (feature) {
    case 'communities':
      return `Community pages are included on ${PRO_AND_UP}. Upgrade to add them.`;
    case 'seo_tools':
      return `SEO tools — site health, AI-written titles and descriptions, and redirects — are included on ${PRO_AND_UP}. ` +
        `You can still write each page's title and description yourself.`;
    case 'custom_domain':
      return `A custom domain is included on every paid plan, from ${STARTER_NAME} up. Upgrade to connect your own domain.`;
  }
}

// ── Sections a plan's site shows (phase 3, Dawn 2026-10-01) — twin of the gateway's
// publicBlockAllowed (compared in its entitlements.mirror.test.ts). The live site doesn't render the
// others, so the builder doesn't offer to add them: Free has no listings, search, maps or MLS
// agent/office rosters; communities need Agent IDX Growth.
const LISTING_BLOCKS = new Set(['listings', 'search', 'search_results', 'map', 'agents', 'offices', 'search-alert']);
const COMMUNITY_BLOCKS = new Set(['communities']);

/** May this plan's site show a section of this type? */
export function blockAllowed(plan: unknown, type: unknown): boolean {
  const t = String(type ?? '');
  if (isLaunchPlan(plan) && t === 'listings') return true;
  if (planRank(plan) < 1 && LISTING_BLOCKS.has(t)) return false;
  if (!featureAllowed(plan, 'communities') && COMMUNITY_BLOCKS.has(t)) return false;
  return true;
}

// Built-in site routes a plan's site serves — twin of the gateway's publicRouteAllowed (compared in
// its entitlements.mirror.test.ts): Free has no listings, search, sold, coming-soon, Team roster,
// buyer account or About page; communities and Recently sold need Agent IDX Growth; a launched
// site shows About.
const LISTING_ROUTES = ['/listings', '/our-listings', '/search', '/sold', '/coming-soon', '/agents', '/offices', '/teams', '/my'];
const FREE_HIDDEN_ROUTES = [...LISTING_ROUTES, '/about'];
const COMMUNITY_ROUTES = ['/communities'];
const SOLD_ROUTES = ['/sold'];
const LAUNCH_ROUTES = ['/about'];

function publicPath(path: unknown): string {
  const raw = String(path ?? '').trim().toLowerCase().split(/[?#]/)[0];
  const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : withSlash;
}

/** May this plan's site serve this built-in path to visitors? */
export function publicRouteAllowed(plan: unknown, path: unknown): boolean {
  const p = publicPath(path);
  const under = (base: string) => p === base || p.startsWith(`${base}/`);
  if (isLaunchPlan(plan) && LAUNCH_ROUTES.some(under)) return true;
  if (planRank(plan) < 1 && FREE_HIDDEN_ROUTES.some(under)) return false;
  if (!featureAllowed(plan, 'communities') && COMMUNITY_ROUTES.some(under)) return false;
  if (planRank(plan) < 2 && SOLD_ROUTES.some(under)) return false;
  return true;
}

/** The words shown where a section the plan doesn't include is offered. */
export function blockMessage(type: unknown): string {
  if (COMMUNITY_BLOCKS.has(String(type ?? ''))) return featureMessage('communities');
  return `Listings, search and maps are included on every paid plan, from ${STARTER_NAME} up. Upgrade to show them on your site.`;
}

// ── Add-ons (Dawn, 2026-09-29) — twin of services/api-gateway/src/lib/addons.ts ───────────────
// AI Marketer and SEO Autopilot: included on Brokerage Growth; a paid add-on on Agent IDX Growth
// ($25/mo each, $40/mo both; $300/$480 a year on annual plans); Free and Essentials upgrade first.

export const ADDON_LABELS = { ai_marketer: 'AI Marketer', seo_autopilot: 'SEO Autopilot' } as const;
export const ADDON_PRICES = { single: { month: 25, year: 300 }, bundle: { month: 40, year: 480 } } as const;

/**
 * The words when BLT isn't on for this account — the twin of bltNotEnabledMessage in
 * services/api-gateway/src/lib/blt-entitlement.ts, tested against the same table, so the Studio
 * and the assistant say the same thing. Whether BLT IS on is never worked out here: the gateway
 * sends that as `tenant.blt_enabled`. BLT isn't sold in the app yet, so we switch the add-on on.
 */
export const BLT_ADDON_PRICE = { month: 50, year: 600 } as const;

export function bltNotEnabledMessage(plan: unknown): string {
  if (addonIncludedInPlan(plan)) return "BLT isn't available on this site.";
  const offer = `BLT is included on ${AGENCY_NAME}, and available on ${PRO_NAME} as a $${BLT_ADDON_PRICE.month}/mo add-on.`;
  return addonPurchasable(plan) ? `${offer} Contact us to add it.` : `${offer} Upgrade to ${PRO_NAME} to add it.`;
}

/** Twin of the gateway's addonIncludedInPlan: the plans that include the add-ons. */
export function addonIncludedInPlan(plan: unknown): boolean {
  return planRank(plan) >= 3;
}

/** Twin of the gateway's addonPurchasable: the one plan that buys them. */
export function addonPurchasable(plan: unknown): boolean {
  return planRank(plan) === 2;
}

/** The words for an account that can't buy it yet (Free/Essentials). */
export function addonUpgradeMessage(addon: keyof typeof ADDON_LABELS): string {
  return `${ADDON_LABELS[addon]} is included on ${AGENCY_NAME}, and available on ${PRO_NAME} as a ` +
    `$${ADDON_PRICES.single.month}/mo add-on. Upgrade to ${PRO_NAME} to add it.`;
}
